import { Typography, Stack } from '@mui/material';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { Currency } from '@/components/general/Currency/Currency';
import { NonTaxable } from '@/components/blocks/wallet/NonTaxable/NonTaxable';
import { usePhrase } from '@/hooks';
import { VATAmount } from '../VATAmount/VATAmount';

import AddCardIcon from '@mui/icons-material/AddCard';
interface PaymentAmountProps {
    transactionData: {
        currencyCode: string;
        currencySymbol: string;
        amount?: number | null;
        currencySymbolPosition: string;
        balanceNonTaxable?: number | null;
    };
    amount?: string | number | null;
    vatAmount?: number | null;
    existingPaymentAmount?: number | null;
    oneRow?: boolean;
    listingView?: boolean;
}

export const PaymentAmount = ({
    transactionData,
    amount,
    vatAmount,
    existingPaymentAmount,
    oneRow = false,
    listingView = false,
}: PaymentAmountProps) => {
    const { t } = usePhrase();

    const parsedAmount = typeof amount === 'string' ? Number(amount) : amount;
    const amountVal = parsedAmount ?? transactionData?.amount ?? 0;
    const existingAmountVal = existingPaymentAmount ?? 0;

    // Use absolute values for payment form (non-listing view)
    // For listing view, preserve original values (can be positive/negative)
    const totalAmountVal = listingView
        ? existingAmountVal + amountVal
        : Math.abs(existingAmountVal) + Math.abs(amountVal);

    const isNonTaxable = amountVal === transactionData?.balanceNonTaxable;

    return (
        <>
            <Stack
                direction={oneRow ? 'row' : 'column'}
                spacing={oneRow ? 1 : 0}
                data-testid="payment_amount"
                sx={{
                    alignItems: oneRow ? 'center' : 'flex-start',
                }}
            >
                {!listingView && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                mt: oneRow ? 1.2 : 0.5,
                            }}
                        >
                            <AddCardIcon sx={{ fontSize: '16px' }} />
                            <Typography variant="greyLabel">
                                {existingPaymentAmount
                                    ? t(WALLET_PHRASES.UPDATED_AMOUNT)
                                    : t(WALLET_PHRASES.AMOUNT)}
                                :
                            </Typography>
                        </Stack>
                    </Stack>
                )}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={(theme) => ({
                            fontWeight: listingView ? '400' : '600',
                            color: listingView
                                ? theme.palette.text.primary
                                : theme.palette.primary.main,
                        })}
                    >
                        <Currency
                            code={transactionData?.currencyCode}
                            symbol={transactionData?.currencySymbol}
                            amount={totalAmountVal}
                            position={transactionData?.currencySymbolPosition}
                        />
                    </Typography>
                    {isNonTaxable && <NonTaxable />}
                </Stack>
            </Stack>
            {vatAmount && (
                <VATAmount
                    code={transactionData?.currencyCode}
                    symbol={transactionData?.currencySymbol}
                    amount={vatAmount}
                    position={transactionData?.currencySymbolPosition}
                    listingView={listingView}
                />
            )}
        </>
    );
};
